import { cn } from '@/lib/utils';

interface SkeletonProps {
  className?: string;
}

/**
 * Base Skeleton component with enhanced shimmer animation
 * Used as building block for all skeleton loaders
 */
export function Skeleton({ className }: SkeletonProps) {
  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-md bg-gray-200',
        'before:absolute before:inset-0',
        'before:-translate-x-full',
        'before:animate-[shimmer_2s_infinite]',
        'before:bg-gradient-to-r',
        'before:from-transparent before:via-white/60 before:to-transparent',
        className
      )}
      aria-busy="true"
      aria-live="polite"
    />
  );
}

/**
 * Property Card Skeleton Loader
 * Matches the layout of PropertyCard component
 */
export function PropertyCardSkeleton() {
  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      {/* Image skeleton */}
      <Skeleton className="h-48 w-full rounded-none" />

      {/* Content skeleton */}
      <div className="p-4 space-y-3">
        {/* Title */}
        <Skeleton className="h-6 w-3/4" />

        {/* Location */}
        <Skeleton className="h-4 w-1/2" />

        {/* Features (bedrooms, bathrooms, area) */}
        <div className="flex gap-4">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-20" />
        </div>

        {/* Buttons */}
        <div className="flex gap-2 pt-2">
          <Skeleton className="h-10 flex-1" />
          <Skeleton className="h-10 w-24" />
        </div>
      </div>
    </div>
  );
}

/**
 * Blog Card Skeleton Loader
 * Matches the layout of BlogCard component
 */
export function BlogCardSkeleton() {
  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      {/* Image skeleton */}
      <Skeleton className="h-48 w-full rounded-none" />

      {/* Content skeleton */}
      <div className="p-6 space-y-3">
        {/* Title */}
        <Skeleton className="h-5 w-3/4" />

        {/* Excerpt lines */}
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />

        {/* Tags */}
        <div className="flex gap-2 pt-2">
          <Skeleton className="h-6 w-16 rounded-full" />
          <Skeleton className="h-6 w-20 rounded-full" />
        </div>

        {/* Meta info (date, read time) */}
        <div className="flex gap-4 pt-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-16" />
        </div>
      </div>
    </div>
  );
}

/**
 * Property Grid Skeleton Loader
 * Shows multiple property cards loading
 */
interface PropertyGridSkeletonProps {
  count?: number;
  className?: string;
}

export function PropertyGridSkeleton({ count = 6, className }: PropertyGridSkeletonProps) {
  return (
    <div className={cn('grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6', className)}>
      {Array.from({ length: count }).map((_, i) => (
        <PropertyCardSkeleton key={i} />
      ))}
    </div>
  );
}

/**
 * Blog Grid Skeleton Loader
 * Shows multiple blog cards loading
 */
interface BlogGridSkeletonProps {
  count?: number;
  className?: string;
}

export function BlogGridSkeleton({ count = 6, className }: BlogGridSkeletonProps) {
  return (
    <div className={cn('grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6', className)}>
      {Array.from({ length: count }).map((_, i) => (
        <BlogCardSkeleton key={i} />
      ))}
    </div>
  );
}

/**
 * Page Skeleton Loader
 * Full-page loading state with centered spinner
 */
interface PageSkeletonProps {
  message?: string;
}

export function PageSkeleton({ message = 'Cargando página...' }: PageSkeletonProps) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center space-y-4">
        <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600" />
        <p className="text-lg text-gray-600">{message}</p>
      </div>
    </div>
  );
}

/**
 * Section Skeleton Loader
 * Loading state for sections with title
 */
interface SectionSkeletonProps {
  title?: string;
  subtitle?: string;
  children?: React.ReactNode;
}

export function SectionSkeleton({ title, subtitle, children }: SectionSkeletonProps) {
  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          {title && (
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
              {title}
            </h2>
          )}
          {subtitle && (
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {subtitle}
            </p>
          )}
        </div>

        {/* Content area */}
        {children || (
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600" />
          </div>
        )}
      </div>
    </section>
  );
}

/**
 * Form Skeleton Loader
 * Loading state for forms
 */
export function FormSkeleton() {
  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      {/* Form fields */}
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-10 w-full" />
        </div>
      ))}

      {/* Submit button */}
      <Skeleton className="h-12 w-full" />
    </div>
  );
}

/**
 * List Item Skeleton Loader
 * Generic skeleton for list items
 */
export function ListItemSkeleton() {
  return (
    <div className="flex items-center space-x-4 p-4 bg-white rounded-lg">
      <Skeleton className="h-12 w-12 rounded-full" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-3 w-1/2" />
      </div>
    </div>
  );
}

/**
 * Text Skeleton Loader
 * Skeleton for text blocks
 */
interface TextSkeletonProps {
  lines?: number;
  className?: string;
}

export function TextSkeleton({ lines = 3, className }: TextSkeletonProps) {
  return (
    <div className={cn('space-y-2', className)}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          className={cn(
            'h-4',
            i === lines - 1 ? 'w-2/3' : 'w-full'
          )}
        />
      ))}
    </div>
  );
}

/**
 * Table Skeleton Loader
 * Loading state for data tables
 */
interface TableSkeletonProps {
  rows?: number;
  columns?: number;
  className?: string;
}

export function TableSkeleton({ rows = 5, columns = 4, className }: TableSkeletonProps) {
  return (
    <div className={cn('w-full space-y-4', className)}>
      {/* Table header */}
      <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton key={`header-${i}`} className="h-8" />
        ))}
      </div>

      {/* Table rows */}
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div
          key={`row-${rowIndex}`}
          className="grid gap-4"
          style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
        >
          {Array.from({ length: columns }).map((_, colIndex) => (
            <Skeleton key={`cell-${rowIndex}-${colIndex}`} className="h-10" />
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * Avatar Skeleton Loader
 * Loading state for user avatars/profile pictures
 */
interface AvatarSkeletonProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

export function AvatarSkeleton({ size = 'md', className }: AvatarSkeletonProps) {
  const sizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-12 w-12',
    lg: 'h-16 w-16',
    xl: 'h-24 w-24',
  };

  return (
    <Skeleton
      className={cn('rounded-full', sizeClasses[size], className)}
    />
  );
}

/**
 * Button Skeleton Loader
 * Loading state for buttons
 */
interface ButtonSkeletonProps {
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  className?: string;
}

export function ButtonSkeleton({ size = 'md', fullWidth = false, className }: ButtonSkeletonProps) {
  const sizeClasses = {
    sm: 'h-8 w-20',
    md: 'h-10 w-24',
    lg: 'h-12 w-32',
  };

  return (
    <Skeleton
      className={cn(
        sizeClasses[size],
        fullWidth && 'w-full',
        className
      )}
    />
  );
}

/**
 * Image Skeleton Loader
 * Loading state for images with aspect ratio preservation
 */
interface ImageSkeletonProps {
  aspectRatio?: 'square' | 'video' | 'portrait' | 'wide';
  className?: string;
}

export function ImageSkeleton({ aspectRatio = 'video', className }: ImageSkeletonProps) {
  const aspectClasses = {
    square: 'aspect-square',
    video: 'aspect-video',
    portrait: 'aspect-[3/4]',
    wide: 'aspect-[21/9]',
  };

  return (
    <Skeleton
      className={cn('w-full rounded-none', aspectClasses[aspectRatio], className)}
    />
  );
}

/**
 * Card with Content Skeleton
 * Combined skeleton for card layouts with image, title, and description
 */
export function CardContentSkeleton() {
  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      <ImageSkeleton aspectRatio="video" />
      <div className="p-6 space-y-4">
        <Skeleton className="h-6 w-3/4" />
        <TextSkeleton lines={2} />
        <div className="flex gap-2 pt-2">
          <ButtonSkeleton size="sm" fullWidth />
          <ButtonSkeleton size="sm" />
        </div>
      </div>
    </div>
  );
}

/**
 * Input Field Skeleton
 * Loading state for form inputs
 */
export function InputSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn('space-y-2', className)}>
      <Skeleton className="h-4 w-24" />
      <Skeleton className="h-10 w-full" />
    </div>
  );
}
